import {
  faBullhorn,
  faBus,
  faCar,
  faCircleXmark,
  faUsers,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  RouteAnnonsApproachKindEnum,
  RouteFillerOptionDTO,
  RouteGapSuggestionsDTO,
  RouteHubStopoverOptionDTO,
  RouteHubStopoverTriggerEnum,
} from 'api/model';
import Select from 'components/inputs/Select';
import SchablonInfo from './SchablonInfo';
import React from 'react';
import styled from 'styled-components';
import {
  describeAnnonsApproach,
  describeAnnonsUnavailable,
  describeFillerUnavailable,
  describeHomeGroup,
  describePersonalTransportUnavailable,
  describeRideAlongConnection,
  formatAnnonsSlots,
  formatClock,
  GapChoice,
} from './selection-helpers';

const Wrapper = styled.div`
  border: 1px solid #ffbf00;
  border-radius: 4px;
  background: rgba(255, 191, 0, 0.08);
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 0.9em;
`;

/** Skjuter info-ikonen till högerkanten, så den inte bryter rubrikens läsriktning. */
const HeaderSpacer = styled.span`
  flex: 1;
`;

/** Bredden följer det valda alternativet — de fasta 180 px klippte "Personlig transport · Buss". */
const RemedySelect = styled(Select)`
  align-self: flex-start;
  max-width: 100%;
`;

const OptionDetail = styled.div`
  margin-left: 26px;
  font-size: 0.85em;
  color: rgb(90, 90, 90);
`;

const CountExplanation = styled.div`
  margin-top: 2px;
  font-size: 0.95em;
  font-style: italic;
  color: rgb(125, 125, 125);
`;

/**
 * Vad siffran i "Utfyllnadsuppdrag · N i korridoren" faktiskt räknar.
 *
 * Frågan "var kommer 14 ifrån?" kom av att raden blandar två tal av olika sort: N är hela
 * träfflistan, medan TB:t gäller toppkandidaten. Dessutom är korridoren ett grovt filter —
 * tvåsiffrigt postnummerprefix, där starten även får ligga nära luckans start
 * (`FillerStartRadiusKm`, så att Eskilstuna 63 hittar bilarna i Strängnäs 64) — och
 * mellanlandade bilar kommer med oavsett bokat datum, eftersom en bil som står på en hub inte
 * har någon meningsfull bokad tid.
 */
const fillerCountExplanation = (filler: RouteFillerOptionDTO) =>
  `${filler.availableCount} oplanerade uppdrag i korridoren ${filler.fromZipPrefix}xxx → ` +
  `${filler.toZipPrefix}xxx (upphämtningen får också ligga nära luckans start) ` +
  'som fälttestaren har behörighet för, plus mellanlandade bilar ' +
  'oavsett bokat datum. Av dem prissätts och verifieras de tre bästa — TB:t i raden gäller ' +
  'den bästa av dem, inte alla.';

/**
 * Vad mellanlandningen faktiskt innebär, i klartext.
 *
 * TL:s beslut 2026-09-17: förslaget ska belysa vad vi tänker göra och varför, och TL tar
 * beslutet. Därför en mening som säger både vinsten (kortare dag) och priset (en sträcka kvar
 * att köra) — inte bara ett alternativ i en lista.
 */
const describeHubStopover = (hub: RouteHubStopoverOptionDTO) => {
  const direction = hub.isOutbound
    ? 'Bilen är på väg ut i landet, så paketet måste granskas av dig innan det verkställs.'
    : 'Bilen går mot logistiken, vilket är normal praxis.';
  // Varför förslaget gavs (TL-feedback 2026-10-13): tåg hem från logistiken ska bara tas i nödfall.
  const reason =
    hub.trigger === RouteHubStopoverTriggerEnum.AvoidsTransitHome
      ? 'Föreslås för att slippa tåget hem från logistiken. '
      : '';

  return `${reason}Bilen ställs på ${hub.hubName} efter ${Math.round(hub.kmToHub)} km. Fälttestaren är hemma ${formatClock(hub.arrivalHomeWithStopover)} i stället för ${formatClock(hub.arrivalHomeWithoutStopover)}. ${Math.round(hub.kmRemaining)} km blir kvar och lämnas OPLANERAT i poolen — nästa dags planering hittar bilen som en mellanlandad bil. ${direction}`;
};

/** Samordnad hemresa: neutral ton — det är en upplysning om andra paket, inte ett val här. */
const HomeGroupBox = styled.div`
  font-size: 0.85em;
  color: rgb(60, 60, 60);
  border-top: 1px dashed rgba(0, 0, 0, 0.15);
  padding-top: 5px;
`;

/** Spärrad samåkning eller inaktuell grupp: samma bruna ton som andra "kräver ditt beslut". */
const HomeRideNotice = styled.div`
  font-size: 0.85em;
  font-weight: 600;
  color: rgb(140, 80, 10);
`;

/** Kronbelopp för lucka-valet: rött = kostnad, grönt = täckningsbidrag. */
const MoneyBadge = styled.span<{ positive?: boolean }>`
  margin-left: 6px;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.92em;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${({ positive }) => (positive ? 'rgb(20,110,60)' : 'rgb(150,40,40)')};
  background: ${({ positive }) => (positive ? 'rgb(228,242,232)' : 'rgb(248,232,230)')};
`;

/**
 * Avresan ligger på ett tidigare dygn. Egen ton — det är varken pengar eller ett fritt val utan
 * ett påstående om när dagen faktiskt börjar, och det är den upplysning klockslagen dolde.
 */
const OvernightBadge = styled.span`
  margin-left: 6px;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.92em;
  font-weight: 600;
  white-space: nowrap;
  color: rgb(140, 80, 10);
  background: rgb(252, 240, 220);
`;

const formatPercent = (value: number | null | undefined): string =>
  value == null ? '?' : value.toLocaleString('sv-SE', { maximumFractionDigits: 1 });

/** Kort märkning av annonsens tillresa i rullgardinens etikett. */
const annonsApproachShort = (kind: RouteAnnonsApproachKindEnum): string => {
  switch (kind) {
    case RouteAnnonsApproachKindEnum.Filler:
      return ' via bil till logistikcentret';
    case RouteAnnonsApproachKindEnum.PersonalTransport:
      return ' via kollektivt till logistikcentret';
    default:
      return '';
  }
};

const formatSek = (value: number): string =>
  `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(Math.round(value)).toLocaleString('sv-SE')} kr`;

/** ok = utrymme kvar, warn = begärda slottar konkurrerar osynligt, over = valen spräcker budgeten. */
type BudgetTone = 'ok' | 'warn' | 'over';

const budgetTones: Record<BudgetTone, { background: string; color: string }> = {
  ok: { background: 'rgba(0,187,0,0.15)', color: 'rgb(0,110,0)' },
  warn: { background: 'rgba(255,191,0,0.25)', color: 'rgb(140,90,0)' },
  over: { background: 'rgb(255,0,0)', color: '#fff' },
};

const BudgetBadge = styled.span<{ tone?: BudgetTone }>`
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 0.8em;
  background: ${({ tone = 'ok' }) => budgetTones[tone].background};
  color: ${({ tone = 'ok' }) => budgetTones[tone].color};
`;

interface Props {
  title: string;
  subtitle?: string;
  gap: RouteGapSuggestionsDTO;
  value: GapChoice;
  onChange: (choice: GapChoice) => void;
  disabled?: boolean;
  /** Live: antal VALDA annonsförfrågningar per destination över alla paket (inkl. denna rad om vald). */
  selectedAnnonsByDestination?: Record<number, number>;
  /**
   * Text som säger att resan avgår dagen innan, från describeEarlierDayDeparture. Klockslaget
   * ensamt döljer det — "20:45 → 05:23" ser ut som en natt i samma dygn.
   */
  earlierDayDeparture?: string | null;
  /** Varför samåkningen inte går igenom eller gruppen inte gäller längre (describeHomeRideStatus). */
  homeRideNotice?: string;
}

/**
 * Åtgärdsväljare för ett gap (första/sista benet).
 *
 * ALLA fyra alternativ visas alltid, även de som inte går att välja — ett saknat alternativ får
 * en avstängd rad med sin orsak i stället för att försvinna. Tidigare renderades persontransport
 * och utfyllnad bara när backend skickat ett förslag, så en lucka som bara kunde lösas med annons
 * såg ut att sakna de andra vägarna helt, fast DTO:n bär orsaken till varför de uteblev.
 *
 * Förvalet sätts av bestGapChoice till alternativet med högst TB, inte till "Gör inget".
 */
const GapRemedySelector: React.FC<Props> = ({
  earlierDayDeparture,
  homeRideNotice,
  title,
  subtitle,
  gap,
  value,
  onChange,
  disabled,
  selectedAnnonsByDestination,
}) => {
  const transit = gap.personalTransport;
  const annons = gap.assignmentRequest;
  const {filler} = gap;
  const hub = gap.hubStopover;
  const ride = gap.rideAlong;

  // "Redan reserverade" = annonser VALDA på andra ruttpaket mot samma destination, live.
  const annonsSelectedHere = value === 'assignmentRequest';
  const annonsSelectedOnOtherPackages = annons
    ? Math.max(
        0,
        (selectedAnnonsByDestination?.[annons.toCompanyId] ?? 0) -
          (annonsSelectedHere ? 1 : 0)
      )
    : 0;
  const annonsOverBudget =
    annons != null &&
    annonsSelectedOnOtherPackages + 1 > annons.remainingDayBudget;
  const annonsSlots = annons ? formatAnnonsSlots(annons) : undefined;
  const annonsSaknasOrsak = describeAnnonsUnavailable(gap);
  const transitSaknasOrsak = describePersonalTransportUnavailable(gap);
  const fillerSaknasOrsak = describeFillerUnavailable(gap);

  const tbText = (tbSek: number | null | undefined) =>
    tbSek == null ? 'TB okänt' : `TB ${formatSek(tbSek)}`;

  // Ordningen är fast och inte sorterad på TB: väljaren ska se likadan ut mellan körningar, och
  // förvalet bär redan informationen om vilket alternativ som är bäst.
  const allOptions: {
    choice: GapChoice;
    label: string;
    title?: string;
    unavailable?: string;
  }[] = [
    {
      choice: 'personalTransport',
      label: transit
        ? `Personlig transport · ${transit.description || 'kollektivt'} · ${Math.round(transit.durationSeconds / 60)} min`
        : 'Personlig transport',
      unavailable: transit ? undefined : transitSaknasOrsak,
    },
    {
      choice: 'assignmentRequest',
      label: annons
        ? `Annonsförfrågan${annonsApproachShort(annons.approachKind)} · ${tbText(annons.tbSek)}${annons.isBelowTruckShareTarget ? ' · under riktvärdet för lastbil' : ''}`
        : 'Annonsförfrågan',
      unavailable: annons ? undefined : (annonsSaknasOrsak ?? 'inget förslag lämnades'),
    },
    {
      choice: 'filler',
      // "N i korridoren" och inte bara "N bilar": siffran är hela träfflistan, medan TB:t i
      // samma rad gäller toppkandidaten. Två tal av olika sort i samma mening väckte frågan
      // "var kommer 14 ifrån?" — ordet "bilar" utan kvalificering inbjöd till den.
      label:
        filler && filler.availableCount > 0
          ? `Bil åt rätt håll · ${filler.availableCount} i korridoren · TB på bäst av 3: ${filler.topCandidateRevenueIsEstimated && filler.topCandidateTbSek != null ? '~' : ''}${tbText(filler.topCandidateTbSek)}`
          : 'Bil åt rätt håll',
      title: filler ? fillerCountExplanation(filler) : undefined,
      unavailable: fillerSaknasOrsak,
    },
    {
      choice: 'rideAlong',
      label: ride
        ? `Samåkning · med ${ride.carrierFieldTesterName} i ${ride.carrierRegistrationNumber ?? 'bil'} · avgång ${formatClock(ride.departureTime)} · sparar ${ride.savedTicketSek} kr`
        : 'Samåkning',
      title: ride
        ? ride.carrierIsFiller
          ? `Åker med i bilen ${ride.carrierFieldTesterName} tar ${ride.fromCity} → ${ride.toCity}. Den finns bara om ${ride.carrierFieldTesterName}s hemresa verkställs som just den bilen, samtidigt.`
          : `Åker med i en bil som ändå körs ${ride.fromCity} → ${ride.toCity}. Restiden är betald oavsett — det är biljetten som sparas. Bärarens paket måste verkställas samtidigt.`
        : undefined,
      unavailable: ride ? undefined : 'ingen bil åt rätt håll',
    },
    {
      choice: 'hubStopover',
      label: hub
        ? `Mellanlandning · ${hub.hubName} · hemma ${formatClock(hub.arrivalHomeWithStopover)} i stället för ${formatClock(hub.arrivalHomeWithoutStopover)}`
        : 'Mellanlandning vid hub',
      title: hub ? describeHubStopover(hub) : undefined,
      unavailable: hub ? undefined : 'ingen hub på vägen',
    },
    {
      choice: 'none',
      label: gap.isIdleWaitOnly
        ? 'Gör inget — fälttestaren väntar till nästa uppdrag'
        : 'Gör inget — fälttestaren tar sig dit på egen hand',
    },
  ];
  // En ren håltimme (kort överföring, lång väntan) handlar om att fylla tiden: bara utfyllnad
  // eller att vänta. Persontransporten till nästa ben skapas som vanligt utanför väljaren.
  const options = allOptions.filter(
    (option) => !gap.isIdleWaitOnly || option.choice === 'filler' || option.choice === 'none'
  );

  return (
    <Wrapper>
      <Header>
        <span>
          {gap.isIdleWaitOnly ? 'Håltimme' : 'Lucka'}: {title}
          {subtitle ? ` · ${subtitle}` : ''}
        </span>
        <HeaderSpacer />
        {/* Beloppen nedan ser exakta ut utan att vara det — ikonen förklarar vilka som är
            mätta mot verkliga utlägg och vilka som fortfarande är uppskattningar. */}
        <SchablonInfo />
      </Header>

      <RemedySelect
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as GapChoice)}
        small
        value={value}
        width="auto"
      >
        {options.map((option) => (
          <option
            key={option.choice}
            disabled={option.unavailable != null}
            title={option.title}
            value={option.choice}
          >
            {option.unavailable != null
              ? `${option.label} — ${option.unavailable}`
              : option.label}
          </option>
        ))}
      </RemedySelect>

      {/* Detaljerna för det VALDA alternativet. De ligger utanför rullgardinen eftersom en
          <option> bara kan bära text — badges, tider och budgetsiffror hade gått förlorade om
          allt skulle rymmas i etiketten. */}
      {value === 'personalTransport' && transit && (
        <OptionDetail>
          <FontAwesomeIcon icon={faBus} />{' '}
          {transit.description || 'Kollektivt'} ·{' '}
          {formatClock(new Date(transit.departureTimeEpoch * 1000))} →{' '}
          {formatClock(new Date(transit.arrivalTimeEpoch * 1000))} ·{' '}
          {Math.round(transit.durationSeconds / 60)} min restid
          {earlierDayDeparture && (
            <>
              {' · '}
              <OvernightBadge title="Fälttestaren måste resa kvällen innan — dagen börjar alltså långt före paketets fönster.">
                {earlierDayDeparture}
              </OvernightBadge>
            </>
          )}
          {transit.estimatedCostSek !== 0 && (
            <>
              {' · '}
              <MoneyBadge title="Biljettschablon + restiden som betald arbetstid">
                {formatSek(transit.estimatedCostSek)}
              </MoneyBadge>
              {' · '}
              biljett {formatSek(-transit.ticketSek)} + betald restid{' '}
              {formatSek(-transit.paidTravelSek)}
            </>
          )}
        </OptionDetail>
      )}

      {value === 'assignmentRequest' && annons && (
        <OptionDetail>
          <FontAwesomeIcon icon={faBullhorn} /> {annons.fromCompanyName} →{' '}
          {annons.toCompanyName} · {formatClock(annons.targetStartTime)}–
          {formatClock(annons.targetEndTime)}
          {annons.tbSek != null && (
            <>
              {' · '}
              <MoneyBadge
                positive={annons.tbSek > 0}
                title={`Täckningsbidrag för hela åtgärden: intäkt ${formatSek(annons.revenueSek ?? 0)} (annonsens interna leveranspris till ${annons.toCompanyName}${annons.approachRevenueSek > 0 ? ' + utfyllnaden på tillresan' : ''}) − kostnad ${formatSek(-annons.costSek)} (annonsbenet ${formatSek(-annons.annonsCostSek)}, tillresan ${formatSek(-annons.approachCostSek)}, vidare från anläggningen ${formatSek(-annons.connectionCostSek)})`}
              >
                TB {formatSek(annons.tbSek)}
              </MoneyBadge>
            </>
          )}
          {annonsSlots && (
            <>
              {' · '}
              <BudgetBadge
                title={
                  annonsSlots.hasRequested
                    ? `Dagens slottar mot ${annons.toCompanyName}. De ${annons.requestedToday} begärda är öppna annonsförfrågningar som ännu inte tilldelats — de dras INTE av budgeten (platsen tas först vid tilldelning), så "kvar" kan vara upptaget av någon annans förfrågan.`
                    : `Dagens slottar mot ${annons.toCompanyName}: kvar = budget − redan tagna.`
                }
                tone={annonsSlots.hasRequested ? 'warn' : 'ok'}
              >
                {annonsSlots.text}
              </BudgetBadge>
            </>
          )}
          {(annonsSelectedHere || annonsSelectedOnOtherPackages > 0) && (
            <>
              {' · '}
              <BudgetBadge tone={annonsOverBudget ? 'over' : 'ok'}>
                totalt {annonsSelectedOnOtherPackages + (annonsSelectedHere ? 1 : 0)}{' '}
                {annonsSelectedOnOtherPackages + (annonsSelectedHere ? 1 : 0) === 1
                  ? 'vald'
                  : 'valda'}
                {annonsSelectedHere ? ' inklusive detta ruttpaket' : ' på andra ruttpaket'}
              </BudgetBadge>
            </>
          )}
          {annons.isBelowTruckShareTarget && (
            <>
              {' · '}
              <BudgetBadge
                title={`Andelen annonsbilar på lastbil till Riddermark-anläggningarna var ${formatPercent(annons.truckSharePercent)} % de senaste 30 dygnen, mot riktvärdet minst ${annons.minTruckSharePercent ?? '?'} %. En annonsförfrågan till fälttestare tar en plats som en lastbil kunde ha fyllt. Förfrågan förväljs därför inte — riktvärdet är ingen spärr.`}
                tone="warn"
              >
                Under riktvärdet för lastbil ({formatPercent(annons.truckSharePercent)} % mot minst{' '}
                {annons.minTruckSharePercent ?? '?'} %)
              </BudgetBadge>
            </>
          )}
          {/* Utan prisrad lämnar backend TB tomt hellre än gissar. Säg det rakt ut — annars
              ser raden ut att sakna ekonomi, vilket är precis den tystnad TB skulle råda bot på. */}
          {annons.tbSek == null && (
            <> · TB okänt — prislistan saknar rad för destinationen</>
          )}
          {/* Annonsen är två steg när fälttestaren inte står vid logistikcentret (paket #49):
              resan dit verkställs tillsammans med förfrågan och ska synas här. */}
          <div>{describeAnnonsApproach(annons)}</div>
        </OptionDetail>
      )}

      {value === 'filler' && filler && (
        <OptionDetail>
          <FontAwesomeIcon icon={faCar} />{' '}
          {filler.topCandidateRegistrationNumber ? (
            <>
              {filler.topCandidateIsStopover ? 'Mellanlandad bil' : 'Oplanerat uppdrag'}:{' '}
              {filler.topCandidateRegistrationNumber} ·{' '}
              {filler.topCandidateFromCity} → {filler.topCandidateToCity}
              {filler.topCandidateBookedTo &&
                ` · bokad till ${formatClock(filler.topCandidateBookedTo)}`}
              {filler.topCandidateTbSek != null && (
                <>
                  {' · '}
                  <MoneyBadge
                    positive={filler.topCandidateTbSek > 0}
                    title="Toppkandidatens täckningsbidrag: prislisteintäkt − arbetstid × timlön"
                  >
                    TB {formatSek(filler.topCandidateTbSek)}
                  </MoneyBadge>
                </>
              )}
              {/* Samma tystnad som annonsraden fick bort: utan prisrad visades ingen TB-badge
                  alls, och raden såg ut att sakna ekonomi i stället för att sakna pris. Väljs
                  den blir dessutom hela paketets TB okänt. */}
              {filler.topCandidateTbSek == null && (
                <> · TB okänt — prislistan saknar rad för uppdraget</>
              )}
            </>
          ) : (
            <>
              Toppkandidat tilldelas direkt (korridor {filler.fromZipPrefix}xxx →{' '}
              {filler.toZipPrefix}xxx)
            </>
          )}
          <CountExplanation>{fillerCountExplanation(filler)}</CountExplanation>
        </OptionDetail>
      )}

      {value === 'rideAlong' && ride && (
        <OptionDetail>
          <FontAwesomeIcon icon={faCar} /> Med {ride.carrierFieldTesterName} i{' '}
          {ride.carrierRegistrationNumber ?? 'bil'} · {ride.fromCity} → {ride.toCity} ·{' '}
          {formatClock(ride.departureTime)}–{formatClock(ride.arrivalTime)}
          {describeRideAlongConnection(ride) && (
            <>
              {' · '}
              {describeRideAlongConnection(ride)}
            </>
          )}
          {' · '}
          <MoneyBadge positive title="Biljettpengar som sparas — restiden är betald oavsett">
            sparar {ride.savedTicketSek} kr
          </MoneyBadge>
          <CountExplanation>
            {ride.carrierIsFiller
              ? `Samordnad hemresa: ${ride.carrierFieldTesterName} kör ${ride.carrierRegistrationNumber ?? 'en bil'} ${ride.fromCity} → ${ride.toCity}, och bilen går när de som åker med är vid den. `
              : `Bilen körs ändå ${ride.fromCity} → ${ride.toCity}, så det finns en plats. `}
            {ride.homeCountyName
              ? `Bilen når hemlänet (${ride.homeCountyName}) men inte hemankaret — ${Math.round(ride.walkToPickupKm)} km egen pendling kvar. `
              : ride.walkToPickupKm > 0
                ? `${Math.round(ride.walkToPickupKm)} km kvar från avstigningen till målet. `
                : ''}
            {ride.carrierIsFiller
              ? `${ride.carrierFieldTesterName}s paket måste verkställas samtidigt och med "Bil åt rätt håll" vald som hemresa — annars finns ingen bil, och valet går inte igenom.`
              : 'Bärarens paket måste verkställas samtidigt — annars finns ingen bil att åka med, och valet går inte igenom.'}
          </CountExplanation>
        </OptionDetail>
      )}

      {value === 'hubStopover' && hub && (
        <OptionDetail>
          <FontAwesomeIcon icon={faWarehouse} /> {hub.hubName}, {hub.hubCity} ·{' '}
          framme {formatClock(hub.arrivalAtHub)} efter {Math.round(hub.kmToHub)} km
          {' · '}
          <MoneyBadge title="Schablonkostnad för sträckan som blir kvar att köra en annan dag">
            {hub.remainingLegCostSek} kr kvar
          </MoneyBadge>
          {hub.isOutbound && (
            <>
              {' · '}
              <OvernightBadge title="Bilen är på väg ut i landet och riskerar att bli stående långt från den som ska hämta den. Paketet är därför alltid manuellt.">
                Kräver ditt beslut
              </OvernightBadge>
            </>
          )}
          <CountExplanation>{describeHubStopover(hub)}</CountExplanation>
        </OptionDetail>
      )}

      {value === 'none' && (
        <OptionDetail>
          <FontAwesomeIcon icon={faCircleXmark} /> Ingen åtgärd skapas — luckan
          lämnas till fälttestaren.
        </OptionDetail>
      )}

      {gap.homeGroup && (
        <HomeGroupBox>
          <FontAwesomeIcon icon={faUsers} /> Hem tillsammans ·{' '}
          {describeHomeGroup(gap.homeGroup)}
        </HomeGroupBox>
      )}

      {homeRideNotice && <HomeRideNotice>{homeRideNotice}</HomeRideNotice>}
    </Wrapper>
  );
};

export default GapRemedySelector;
